/**
 * InfoBlock — the one card every titled info block on a spine page is made of.
 *
 * Profile grew its blocks one at a time and each brought its own chrome: the
 * hundo dex had a green-to-white gradient and a green heading, the raid and PvP
 * panels had a flat raised surface and an ink heading, the shiny-perfect panel
 * had the same surface with a different border token, the post-event catch-up
 * card had a neon card and an eyebrow PILL for a heading, and the PvP strip had
 * no card at all. Every one of them was defensible on its own and the page read
 * as five products stacked on top of each other.
 *
 * So the surface, the border, the padding, the heading type and the spine dot
 * all live here and nowhere else. A block that wants to look different has to
 * change this file, which is the point: the next block cannot drift by being
 * written from scratch.
 *
 * WHAT IT IS NOT: a hero. The page header (MenuHeading / Profile's hero card)
 * keeps its eyebrow pill, its gradient and its display type — a pill belongs at
 * the top of a page, saying which page it is, and nowhere below that.
 *
 * The spine dot is drawn by the block itself rather than by DotRail or
 * Profile's SpineSection, because every one of these lives behind a component
 * boundary where the automatic dot cannot reach it (see SpineDot). Callers put
 * the block inside the page's `pl-5` gutter and get the dot for free; a caller
 * outside a gutter passes `dot={false}`.
 */
import { createContext, useContext, useEffect, type ReactNode } from "react";
import { ChevronDown, type LucideIcon } from "lucide-react";
import { SpineDot } from "@/components/SectionHeadingDot";

/**
 * Collapse support (2026-09-19 owner ask): on the Profile spine every block
 * can fold to its heading row, and the choice is remembered per block (see
 * lib/profileBlockPrefs). The page provides this context; a block rendered
 * anywhere else (or in a component test) sees null and renders exactly as
 * before, with no chevron.
 */
export interface InfoBlockCollapse {
  isOpen: (key: string) => boolean;
  toggle: (key: string) => void;
  /** Tell the page a block exists, so "collapse all" knows its keys. Returns the unregister. */
  register: (key: string) => () => void;
}

export const InfoBlockCollapseContext = createContext<InfoBlockCollapse | null>(null);

/**
 * Block titles are 16px with 0.18em tracking on every phone from 390pt up,
 * the size the Profile spine was designed at. Both ease down toward 320pt so
 * a title beside its icon, chevron and trailing chip ("SHINY HUNT" + Evo)
 * still fits an iPhone with Display Zoom on (320pt) instead of ending in an
 * ellipsis (device-scaling pass, 2026-10-23: at 320pt the title had 114px
 * and needed 129px). clamp() resolves to the canonical values at 390pt, so
 * nothing moves on the phones we test on.
 */
export const INFO_BLOCK_TITLE_FONT_SIZE = "clamp(13px, 4.29vw - 0.71px, 16px)";
export const INFO_BLOCK_TITLE_TRACKING = "clamp(0.04em, 2.97vw - 8.7px, 0.18em)";

interface InfoBlockProps {
  /** Lucide icon on the heading row. Omit for a block with no icon. */
  icon?: LucideIcon;
  /** The block's heading. Rendered as the canonical uppercase h2. */
  title: string;
  /** Optional supporting line under the heading. */
  subtitle?: ReactNode;
  /** Right-hand slot on the heading row — a stat chip, a link. */
  trailing?: ReactNode;
  /** Draw the gutter spine dot. On by default; off for blocks outside a rail. */
  dot?: boolean;
  className?: string;
  id?: string;
  "data-testid"?: string;
  /** Extra data-* attributes (e.g. data-variant) some blocks are keyed by. */
  dataAttrs?: Record<string, string>;
  /**
   * The key the block's open/closed state is stored under when a collapse
   * context is present. Defaults to data-testid, then the title.
   */
  collapseKey?: string;
  children: ReactNode;
}

/**
 * The canonical heading row: 8px dot in the gutter, a 16px icon in the accent,
 * and the title in the adaptive ink colour at one size, one weight, one
 * tracking. No per-block hue — a green heading on one card and an ink heading
 * on the next is the drift this replaces.
 */
export function InfoBlock({
  icon: Icon,
  title,
  subtitle,
  trailing,
  dot = true,
  className = "",
  id,
  "data-testid": testId,
  dataAttrs,
  collapseKey,
  children,
}: InfoBlockProps) {
  const collapse = useContext(InfoBlockCollapseContext);
  const key = collapseKey ?? testId ?? title;
  useEffect(() => (collapse ? collapse.register(key) : undefined), [collapse, key]);
  const collapsible = collapse != null;
  const open = !collapsible || collapse.isOpen(key);

  const titleRow = (
    <div className="flex items-center gap-2 min-w-0">
      {Icon && (
        <Icon className="w-4 h-4 shrink-0 text-[var(--accent-fill)]" aria-hidden />
      )}
      <h2
        className="font-semibold uppercase text-[var(--text-primary)] truncate"
        style={{ fontSize: INFO_BLOCK_TITLE_FONT_SIZE, letterSpacing: INFO_BLOCK_TITLE_TRACKING }}
      >
        {title}
      </h2>
      {collapsible && (
        <ChevronDown
          className="w-4 h-4 shrink-0 transition-transform text-[var(--text-tertiary)]"
          style={{ transform: open ? "none" : "rotate(-90deg)" }}
          aria-hidden
        />
      )}
    </div>
  );

  return (
    <section
      id={id}
      data-testid={testId}
      {...dataAttrs}
      data-collapsed={collapsible ? String(!open) : undefined}
      className={`relative rounded-2xl ${open ? "p-4" : "px-4 py-2.5"} bg-[var(--surface-raised)] border border-[var(--surface-chip-border)] ${className}`}
    >
      {dot && <SpineDot />}
      <header className={`${open ? "mb-3" : ""} flex items-start justify-between gap-3`}>
        <div className="min-w-0 flex-1">
          {collapsible ? (
            // The whole title row is the toggle: a 44px-tall target on the
            // one line every block already has, no extra chrome.
            <button
              type="button"
              onClick={() => collapse.toggle(key)}
              aria-expanded={open}
              data-testid={`info-block-toggle-${key}`}
              className="w-full text-left -my-1 py-1"
            >
              {titleRow}
            </button>
          ) : (
            titleRow
          )}
          {open && subtitle && (
            <p className="mt-1 text-xs leading-relaxed text-[var(--text-secondary)]">
              {subtitle}
            </p>
          )}
        </div>
        {open && trailing && <div className="shrink-0">{trailing}</div>}
      </header>
      {open && children}
    </section>
  );
}

/**
 * The stat chip two blocks hang off their heading row (hundo total, shiny
 * perfect count). Same recipe both places rather than two near-identical
 * inline-styled copies.
 */
export function InfoBlockStat({
  label,
  value,
  "data-testid": testId,
}: {
  label: string;
  value: ReactNode;
  "data-testid"?: string;
}) {
  return (
    <div
      className="rounded-[var(--radius-lg)] border bg-[var(--surface-card)] px-3 py-1.5 text-center"
      style={{
        borderColor: "color-mix(in srgb, var(--accent-fill) 22%, var(--border-subtle))",
        boxShadow: "0 8px 32px color-mix(in srgb, var(--accent-fill) 14%, transparent)",
      }}
    >
      <div className="text-[10px] uppercase tracking-[0.16em] text-[var(--text-tertiary)]">
        {label}
      </div>
      <div
        className="text-lg font-bold tabular-nums text-[var(--text-primary)]"
        data-testid={testId}
      >
        {value}
      </div>
    </div>
  );
}
